import React, { useState, useEffect, useRef } from 'react';
import {
  Form,
  Button,
  Alert,
  // FormControlProps,
} from 'react-bootstrap';
import { API_ROUTES } from '../../config/api-routes';
import { validationMessages } from '../../config/validationMessages';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ReactComponent as BackIcon } from '../../images/arrow-back.svg';
import './Complete-Profile-Avatar.css';
import axiosInstance from '../../api/api';
import OnboardingProgress from '../OnboardingProgress';
import { PageBusyLoader } from '../ui/PageBusyLoader';
import { getSessionStore } from '../../store/sessionStore';

interface ProfileFormState {
  profileImg: File | null;
}

const { FILE_EXCEEDED_LIMIT_8MB, UPLOAD_IMAGE } = validationMessages;

const CompleteProfileForm: React.FC = () => {
  const [profileFormData, setProfileFormData] = useState<ProfileFormState>({
    profileImg: null,
  });

  const [loader, setLoader] = useState<boolean>(false);
  const [fetchError, setFetchingError] = useState<boolean>(false);
  const [noAvatarError, setNoAvatarError] = useState<string>('');
  const [isDragActive, setIsDragActive] = useState<boolean>(false);
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [searchParams] = useSearchParams();
  const isEdit = searchParams.get('mode') === 'edit';
  const referrer = searchParams.get('referrer');

  const getUserInfo = async () => {
    try {
      const response = await axiosInstance.get('user/current-user');

      if (!response || response.status !== 200) {
        setLoader(false);
        return;
      }

      // const data = response.data;
    } catch (error: any) {
      console.error('Error fetching user info:', error);
    } finally {
      setLoader(false);
    }
  };
  useEffect(() => {
    getUserInfo();
  }, []);

  useEffect(() => {
    if (profileFormData.profileImg) {
      setNoAvatarError('');
    }
  }, [profileFormData.profileImg]);

  const validateAndSetFile = (file: File | null) => {
    if (!file) return;
    setFetchingError(false);
    const maxSizeInBytes = 8 * 1024 * 1024;

    if (file.size > maxSizeInBytes) {
      setNoAvatarError(`${FILE_EXCEEDED_LIMIT_8MB}`);
      return;
    }

    const validImageTypes = ['image/jpeg', 'image/png', 'image/gif'];
    if (!validImageTypes.includes(file.type)) {
      setNoAvatarError('Please select a valid image file (JPEG, PNG, GIF)');
      return;
    }

    setNoAvatarError('');
    setProfileFormData((prev) => ({
      ...prev,
      profileImg: file,
    }));
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    validateAndSetFile(event.target.files?.[0] || null);
    event.target.value = '';
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragActive(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragActive(false);
    const droppedFile = event.dataTransfer.files?.[0] || null;
    validateAndSetFile(droppedFile);
  };

  const triggerFileUpload = () => {
    fileInputRef.current?.click();
  };

  const handleSubmitAvatar = async (event: any) => {
    event.preventDefault();

    // Fetch current user data
    let response;
    try {
      response = await axiosInstance.get('user/current-user');
      if (!response || response.status !== 200) {
        setLoader(false);
        setFetchingError(true);
        return;
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      setLoader(false);
      setFetchingError(true);
      return;
    }

    const data = response.data;

    // ✅ Check if the user is a recruiter first
    if (data?.user?.role === 'recruiter') {
      handleSubmit(); // Call submit function for recruiter
      return; // ✅ Ensure execution stops here for recruiters
    }

    if (!profileFormData.profileImg) {
      event.preventDefault();
      setNoAvatarError(`${UPLOAD_IMAGE}`);
      return;
    }
    if (isEdit) {
      try {
        const formData = new FormData();
        formData.append('profileImg', profileFormData.profileImg);
        setLoader(true);
        const response = await axiosInstance.put(API_ROUTES.USER.UPDATE_USER, formData);

        if (response && response.status === 200) {
          getSessionStore().updateCurrentUser({
            profileImg:
              response.data?.profileImg ||
              response.data?.user?.profileImg ||
              response.data?.data?.profileImg,
          });
          window.dispatchEvent(new Event('profile-updated'));
          if (referrer) {
            navigate(`/${referrer}`, { replace: true });
          } else {
            navigate('/user-profile', { replace: true });
          }
        } else {
          setFetchingError(true);
        }
      } catch (error: any) {
        console.error('Error updating user avatar:', error.message);
        setLoader(false);
        setFetchingError(true);
      }
    } else {
      setLoader(false);
    }
  };

  const handleSubmit = async (e?: any) => {
    e?.preventDefault();
    const response = await axiosInstance.get('user/current-user');

    if (!response || response.status !== 200) {
      setLoader(false);
      return;
    }

    const data = response.data;

    if (profileFormData.profileImg) {
      try {
        const formData = new FormData();
        formData.append('profileImg', profileFormData.profileImg);
        formData.append('step', '2');

        const response = await axiosInstance.post(API_ROUTES.AUTH.COMPLETE_PROFILE, formData);

        if (response && response.status === 200 && data?.user?.role === 'candidate') {
          if (referrer) {
            navigate(`/${referrer}`, { replace: true });
          } else {
            navigate('/create-prompt', { replace: true });
          }
        } else if (response && response.status === 200 && data?.user?.role === 'recruiter') {
          navigate('/user-profile');
        } else {
          setFetchingError(true);
        }
      } catch (error: any) {
        console.error('Error completing profile:', error.message);
        setFetchingError(true);
      } finally {
      }
    }

    setProfileFormData({
      profileImg: null,
    });
  };

  return (
    <>
      {loader ? (
        <PageBusyLoader pageName="Onboarding" />
      ) : (
        <div className="container-fluid h-100">
          <div className="row g-0 h-100 align-items-center justify-content-center">
            <div className="col-12 px-0">
              <OnboardingProgress />
            </div>
            <div className="container">
              <div className="upload-content-shell">
                {isEdit && (
                  <button
                    title="back icon"
                    onClick={() => {
                      if (referrer) {
                        navigate(`/${referrer}`, { replace: true });
                      } else {
                        navigate('/user-profile', { replace: true });
                      }
                    }}
                    className="go-back-btn upload-back-btn"
                  >
                    <BackIcon width={50} height={50} />
                  </button>
                )}
                <h2 className="title-dash flex2 upload-photo-title">Upload Your Photo</h2>
                <p className="upload-photo-subtitle">
                  Let&apos;s put a face to your SkipScreen profile.
                </p>
                <Form onSubmit={handleSubmitAvatar} className="file-upload-form">
                  <Form.Control
                    type="file"
                    onChange={handleFileChange}
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="fileUpload"
                    ref={fileInputRef}
                  />
                  <div className="upload-photo-grid">
                    <div className="upload-preview-card">
                      {profileFormData.profileImg ? (
                        <div className="position-relative">
                          <img
                            src={URL.createObjectURL(profileFormData.profileImg)}
                            alt="Preview"
                            className="uploaded-image"
                          />
                          <div className="change-image-indicator">
                            <p className="text-muted">Click to change image</p>
                          </div>
                        </div>
                      ) : (
                        <div className="upload-preview-placeholder">No photo selected</div>
                      )}
                      <Button variant="success" type="button" onClick={triggerFileUpload}>
                        {profileFormData.profileImg ? 'Change Photo' : 'Select Photo'}
                      </Button>
                    </div>
                    <div
                      className="file-upload w-100"
                      onDragOver={handleDragOver}
                      onDragEnter={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
                      <div
                        className={`upload-area text-center ${isDragActive ? 'drag-active' : ''}`}
                        role="button"
                        tabIndex={0}
                        onClick={triggerFileUpload}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            triggerFileUpload();
                          }
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="32"
                          height="32"
                          fill="currentColor"
                          className="bi bi-cloud-upload icon-upload"
                          viewBox="0 0 16 16"
                        >
                          <path d="M8 0a5.53 5.53 0 0 0-5.5 5.5c0 1.36.68 2.605 1.84 3.351A2.75 2.75 0 0 0 5 15h6a2.75 2.75 0 0 0 1.66-5.149 3.502 3.502 0 0 0 1.34-2.851A5.53 5.53 0 0 0 8 0zM6 8.5v3h1v-3h1.5v3H10v-3h1.5v3h.5a1.25 1.25 0 0 0 .774-2.287 1.25 1.25 0 0 0-.612-.252l-.028.003A4.505 4.505 0 0 0 8 5 4.505 4.505 0 0 0 6 8.5z" />
                        </svg>
                        <p>
                          {isDragActive ? 'Drop your image here' : 'Drag & drop or click to upload'}
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-center text-muted" style={{ fontSize: '0.875rem' }}>
                    Max file size: 8MB
                  </p>
                  {noAvatarError && <Alert variant="danger">{noAvatarError}</Alert>}
                  {fetchError && (
                    <Alert variant="danger" style={{ marginBottom: '50px' }}>
                      {validationMessages.DATA_FETCH_FAILED}
                    </Alert>
                  )}
                  <Button variant="success" type="submit" className="btn-complete">
                    Submit
                  </Button>
                </Form>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CompleteProfileForm;
